import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { FunnelChart, Funnel, LabelList, Tooltip, ResponsiveContainer } from "recharts";

const STAGES = ["new", "contacted", "qualified", "booked", "converted", "lost"] as const;
const COLORS = ["#002D68", "#143D7C", "#1C4F95", "#A88B5C", "#0E7C5A", "#B43A3A"];

export const Route = createFileRoute("/agent/pipeline")({
  head: () => ({ meta: [{ title: "Pipeline — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentPipeline />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentPipeline() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Array<{ stage: string; created_at: string; updated_at: string }>>([]);

  useEffect(() => { if (user) void load(); }, [user]);
  async function load() {
    const { data } = await supabase.from("leads").select("stage, created_at, updated_at").eq("assigned_agent_id", user!.id);
    setLeads((data ?? []) as any);
  }

  const data = useMemo(() => {
    const counts = STAGES.map(s => leads.filter(l => l.stage === s).length);
    return STAGES.map((s, i) => ({ name: s, value: counts[i], fill: COLORS[i] })).filter(d => d.value > 0);
  }, [leads]);

  const rates = useMemo(() => {
    const c = STAGES.map(s => leads.filter(l => l.stage === s).length);
    const total = leads.length || 1;
    return STAGES.map((s, i) => {
      const avgDays = (() => {
        const items = leads.filter(l => l.stage === s);
        if (!items.length) return 0;
        const ms = items.reduce((sum, l) => sum + (new Date(l.updated_at).getTime() - new Date(l.created_at).getTime()), 0) / items.length;
        return Math.round(ms / (1000 * 60 * 60 * 24));
      })();
      return { stage: s, count: c[i], pct: ((c[i] / total) * 100).toFixed(1), avgDays };
    });
  }, [leads]);

  return (
    <PortalPage eyebrow="Production" title="Pipeline" intro={`${leads.length} active lead${leads.length === 1 ? "" : "s"} across your funnel.`}>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
          <h3 className="font-display text-navy-800 text-[20px] mb-4">Funnel</h3>
          {data.length === 0 ? <p className="text-charcoal-500 text-[13px]">No leads yet.</p> : (
            <ResponsiveContainer width="100%" height={320}>
              <FunnelChart><Tooltip /><Funnel dataKey="value" data={data}><LabelList position="right" dataKey="name" /></Funnel></FunnelChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6">
          <h3 className="font-display text-navy-800 text-[20px] mb-4">Conversion by stage</h3>
          <table className="w-full text-[13px]">
            <thead className="text-left text-charcoal-500 text-[10px] uppercase tracking-[0.12em]"><tr><th className="py-2">Stage</th><th>Count</th><th>%</th><th>Avg days</th></tr></thead>
            <tbody>{rates.map(r => <tr key={r.stage} className="border-t border-bronze-600/10"><td className="py-2 text-navy-800 capitalize">{r.stage}</td><td>{r.count}</td><td>{r.pct}%</td><td>{r.avgDays}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </PortalPage>
  );
}
